import { fetchJSON, postJSON, delJSON } from '@haina/utils-request'

/**
 * 模板管理中心 - 接口 action
 *
 * 后端接口约定：
 *   GET    /ht_templates/list              获取模板列表（模板文件扫描，含 group / type / update_time）
 *   GET    /ht_templates/page           获取模板页面（测试预览用，参数 coli_sn / dei_sn / type / lgc）
 *   GET    /ht_templates/page/list      获取页面版本列表（按 coli_sn + type，含 content，id 倒序）
 *   POST   /ht_templates/page           保存页面（有 id 更新，无 id 新增）
 *   DELETE /ht_templates/page/:id       删除页面（按 id）
 *   POST   /ht_templates/generate       生成信函（新建，参数 coli_sn / dei_sn / type / lgc）
 *
 * 模板以项目文件 {事业部code}_{类型}.html 管理（git 版本控制），事业部 code 与 dei_sn 的映射
 * 配置在后端 config.py 的 DEI_GROUP。不提供模板 CRUD 接口。
 * 错误处理：@haina/utils-request 在 errcode !== 0 或 HTTP 非 2xx 时已抛出含完整信息的 Error
 * （业务错误为 `errmsg: errcode`，HTTP 错误为 `Fetch error: <url> <status>`），
 * 故此处各 action 不再做兜底，错误向上抛给调用方，由界面把完整信息展示给用户。
 */

const BASE = import.meta.env.DEV
  ? 'http://localhost:8013/ht_templates'
  : 'https://p9axztuwd7x8a7.mycht.cn/ht3-templates/ht_templates'

/**
 * 获取模板列表
 * @param {object} params { dei_sn?, type? }
 * @returns {Promise<Array>}
 */
export const getTemplateListAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/list`, params)
  return result || []
}

/**
 * 获取模板页面（测试预览用，按订单渲染信函内容）
 * @param {object} params { coli_sn, dei_sn, type, lgc }
 * @returns {Promise<object>}
 */
export const getTemplatePageAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/page`, params)
  return result || {}
}

/**
 * 根据订单号查询订单序号
 * @param {string} coli_id
 * @returns {Promise<object>}
 */
export const resolveColiSnAction = async (coli_id) => {
  const { result } = await fetchJSON(`${BASE}/resolve-order`, { coli_id })
  return result || {}
}

/**
 * 获取页面版本列表（按 coli_sn + type，含 content，id 倒序）
 * @param {object} params { coli_sn, type }
 * @returns {Promise<Array>}
 */
export const getPageListAction = async (params = {}) => {
  const { result } = await fetchJSON(`${BASE}/page/list`, params)
  return result || []
}

/**
 * 保存页面（有 id 更新，无 id 新增）
 * @param {object} body { id, coli_sn, dei_sn, type, title, content, tour_title, tour_summary, amount, link }
 */
export const savePageAction = async (body) => {
  const { result } = await postJSON(`${BASE}/page`, body)
  return result || {}
}

/**
 * 删除页面（按 id）
 * @param {number|string} id
 */
export const deletePageAction = async (id) => {
  const { result } = await delJSON(`${BASE}/page/${id}`, {})
  return result || {}
}

/**
 * 生成信函（新建）
 * @param {object} params { coli_sn, dei_sn, type, lgc }
 * @returns {Promise<object>}
 */
export const generateTemplateAction = async (params = {}) => {
  const { result } = await postJSON(`${BASE}/generate`, params)
  return result || {}
}
